import React, { useState } from 'react';
import { Card, Form, Button, Input, message } from 'antd';
import { StylesProps, RouterProps } from 'src/library/types';
import { MOBILE_BREAK_POINT } from 'src/globals';
import GenericHoc from 'src/common/generic-hoc';
import { HocOptions } from 'src/common/generic-hoc.types';
import LogoUrl from 'src/assets/logos/shipsyLogo.png';
import { generateOTP, verifyLogin } from 'src/api/auth';
import useInterval from 'src/library/hooks/useInterval';
import { vehicleRequestsRoute } from 'src/routing/routeConstants';
import { loadInitialData } from 'src/common/actions/loadInitialData';

const FormItem = Form.Item;
const RESEND_OTP_TIME_LIMIT = 30;

const styles = (theme: any) => ({
  mainDiv: {
    minHeight: '100vh',
    backgroundColor: theme.bodyBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  header: {
    marginTop: '20px',
    color: '#2F2D3B',
    fontSize: 18,
  },
  cardDiv: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: {
      left: '20px',
      right: '20px',
      bottom: '20px',
    },
  },
  cardStyle: {
    width: '600px',
    border: 'none',
  },
  inputDiv: {
    display: 'flex',
    minWidth: '343px',
    marginTop: 20,
  },
  submitButton: {
    height: '40px',
    marginTop: '10px',
    width: '100%',
    maxWidth: '343px',
  },
  link: {
    color: '#165492',
    fontWeight: 600,
  },
  [`@media (max-width: ${MOBILE_BREAK_POINT}px)`]: {
    mainDiv: {
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    header: {
      fontSize: 16,
    },
    cardStyle: {
      width: '100%',
      border: 'none',
    },
    inputDiv: {
      width: '100%',
    },
  },
});

interface Props extends StylesProps<ReturnType<typeof styles>>, RouterProps {
  dispatch: (fn: (dispatch: any, getState: any) => Promise<void>) => void;
  t: any;
}

const Login: React.FC<Props> = (props: Props) => {
  const { classes, history, dispatch, t } = props;

  const [form] = Form.useForm();
  const [verifyPage, setVerifyPage] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [resendTime, setResendTime] = useState<number>(0);

  const generateOTPHandle = async () => {
    setLoading(true);
    setResendTime(RESEND_OTP_TIME_LIMIT);
    const response = await generateOTP({
      contactNumber: form.getFieldValue('mobile_number'),
    });
    if (response.isSuccess) {
      form.setFieldsValue({ otp: '' });
      setVerifyPage(true);
    } else {
      message.error(response.errorMessage ?? 'Something went wrong!');
      setResendTime(0);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (verifyPage) {
      // verify OTP
      setLoading(true);
      const response = await verifyLogin({
        contactNumber: form.getFieldValue('mobile_number'),
        otp: form.getFieldValue('otp'),
      });
      if (response.isSuccess) {
        await dispatch(loadInitialData());
        history.replace(vehicleRequestsRoute);
      }
      setLoading(false);
    } else {
      generateOTPHandle();
    }
  };

  useInterval(
    () => {
      setResendTime((time) => time - 1);
    },
    verifyPage && resendTime > 0 ? 1000 : null,
  );

  const mobileNumber = form.getFieldValue('mobile_number');

  return (
    <div className={classes.mainDiv}>
      <div className={classes.contentDiv}>
        <Card className={classes.cardStyle} bordered={false}>
          <Form form={form} onFinish={handleSubmit} className="login-form">
            <div className={classes.cardDiv}>
              <img
                style={{
                  height: 'auto',
                  maxWidth: '246px',
                  maxHeight: '100px',
                  objectFit: 'contain',
                }}
                src={window.localStorage.getItem('logoUrl') || LogoUrl}
                alt="Shipsy"
              />
              {verifyPage && (
                <>
                  <p className={classes.header}>{t('Please enter the OTP to verify your account')}</p>
                  <p style={{ color: '#333' }}>
                    {t('One Time Password has been sent to')}
                    {` ${mobileNumber.substring(0, 3)}****${mobileNumber.substring(7)}`}
                  </p>
                  <Button className={classes.link} type="link" onClick={() => setVerifyPage(false)}>
                    {t('Entered wrong mobile number?')}
                  </Button>
                </>
              )}

              <div className={classes.inputDiv}>
                <FormItem
                  name={verifyPage ? 'otp' : 'mobile_number'}
                  rules={[
                    {
                      required: true,
                      message: t(verifyPage
                        ? 'Please enter OTP to proceed'
                        : 'Please enter Mobile Number to proceed'),
                    },
                    {
                      pattern: verifyPage ? /\d{4}/ : /(6|7|8|9)\d{9}/,
                      message: t(verifyPage ? 'Enter 4 digit OTP' : 'Enter valid Mobile Number'),
                    },
                  ]}
                  style={{ width: '100%' }}
                >
                  <Input
                    bordered={false}
                    prefix={verifyPage ? null : '+91'}
                    size="large"
                    placeholder={t(verifyPage ? 'Enter OTP' : 'Enter Mobile Number')}
                    maxLength={verifyPage ? 4 : 10}
                    style={{
                      border: 'none',
                      width: '100%',
                      borderBottom: '1px solid #979797',
                    }}
                  />
                </FormItem>
              </div>
              <Button
                type="primary"
                className={classes.submitButton}
                htmlType="submit"
                loading={loading}
              >
                {t(verifyPage ? 'VERIFY' : 'SEND OTP')}
              </Button>
              {verifyPage &&
                (resendTime === 0 ? (
                  <Button
                    className={classes.link}
                    style={{ marginTop: 20 }}
                    type="link"
                    onClick={generateOTPHandle}
                  >
                    {t('Resend OTP')}
                  </Button>
                ) : (
                  <p style={{ cursor: 'not-allowed', marginTop: 24, color: '#333' }}>
                    {t('Resend OTP in')}
                    {` ${resendTime}s`}
                  </p>
                ))}
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
  },
  connectJss: {
    useJss: true,
    styleSheet: styles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(Login);
